/**
 * AtlasHelpModule — the Cosmos field manual (2026-10-05: the Help window redesigned as a book).
 *
 * A bound handbook rather than a tab list: six parts, ~24 numbered chapters, ~150 pages with folios; a contents page
 * (dot leaders to page numbers), a spine that always shows where you are, a running head, page turns that name the next
 * page, cross-references, a back-of-book index, bookmarks (the ribbon) and a ranked full-text search with snippets.
 * Content: `lib/help/ch_*.ts`; navigation, search and the index: `lib/help/nav.ts` (pure, `verify:help-book`).
 *
 * Opening: H, the ? button, the menu bar, or `bus.emit('atlas:open-help', { page: 'vehicles/hovercraft' })`.
 * Closing: the × (`[data-atlas-close]` — Esc and the pad's B close the top nav panel through the one Esc order), H, or a
 * click outside the book. The root is a `[data-atlas-nav]` panel (gamepad focus navigation; the shoulders walk the
 * Contents / Index / Marks tabs) and wears the theme's window material (`[data-ax-window]`): colours only from tokens.
 *
 * Keys inside the book: ← → (PageUp / PageDown) turn pages, Home = contents, / = search, Backspace = back; Enter in the
 * search opens the best match and ↓ walks the results. Keys pressed inside the book never reach the game.
 */
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { keyBindingText, keyCaptureActive, KEY_ACTIONS, type KeyActionName } from '../lib/keybinds.js';
import { BOOK, FIRST_PAGE } from '../lib/help/book.js';
import {
  flatten, indexOfPage, chapterFolios, search, buildIndex, indexLetters, letterOf, fillKeys, reviveState,
  type FlatPage, type BookState, type IndexEntry, type SearchHit,
} from '../lib/help/nav.js';
import type { App, IModule } from 'enginex';

type View = 'contents' | 'page' | 'index' | 'marks' | 'search';
interface Place { view: View; page: number }

const STORE = 'atlas:help-book';
const START_HERE = ['welcome/manual', 'welcome/new', 'around/keys', 'welcome/starter', 'account/signin', 'maps/waypoints', 'combat/defend'];

const keyText = (a: string): string | null =>
  (KEY_ACTIONS as readonly string[]).includes(a) ? keyBindingText(a as KeyActionName) : null;

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
}

export class AtlasHelpModule implements IModule {
  readonly name = 'AtlasHelp';
  private root: HTMLDivElement | null = null;
  private book: HTMLDivElement | null = null;
  private spine: HTMLElement | null = null;
  private leaf: HTMLElement | null = null;
  private searchInput: HTMLInputElement | null = null;
  private tabs: Record<'contents' | 'index' | 'marks', HTMLButtonElement> | null = null;
  private backBtn: HTMLButtonElement | null = null;

  private flat: FlatPage[] = [];
  private index: IndexEntry[] = [];
  private hits: SearchHit[] = [];
  private place: Place = { view: 'contents', page: 0 };
  private history: Place[] = [];
  private beforeSearch: Place | null = null;
  private state: BookState = { last: null, marks: [] };

  init(_app: App): void {
    this.rebuild();
    this.state = this.loadState();
    this.buildButton();
    this.buildBook();
    window.addEventListener('keydown', (e) => {
      if (this.isOpen()) return; // inside the book its own handler answers (and stops the press)
      const tag = (e.target as HTMLElement | null)?.tagName ?? '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      // A rebind in progress owns the press (space campaign B): H offered to a binding is refused by name in the
      // Controls menu — it must not also open the manual over it.
      if (keyCaptureActive() || e.defaultPrevented) return;
      // !e.repeat: a HELD H opened the book, its repeats closed and re-opened it (or typed "hhh" into the search box).
      if ((e.key === 'h' || e.key === 'H') && !e.repeat && !e.ctrlKey && !e.metaKey && !e.altKey) { e.preventDefault(); this.open(); }
    });
    bus.on('atlas:open-help', (p) => this.open(p?.page));
    const w = window as unknown as { __help?: unknown };
    w.__help = {
      open: (id?: string) => this.open(id),
      close: () => this.close(),
      state: () => ({
        open: this.isOpen(), view: this.place.view, page: this.flat[this.place.page]?.page.id ?? null,
        folio: this.place.page + 1, pages: this.flat.length, marks: [...this.state.marks], hits: this.hits.length,
        terms: this.index.length,
      }),
      search: (q: string) => { if (this.searchInput) { this.searchInput.value = q; this.onSearch(); } return this.hits.length; },
      /** Probe lever: forget the last page, the marks and the history. */
      reset: () => { this.state = { last: null, marks: [] }; this.saveState(); this.history = []; this.place = { view: 'contents', page: 0 }; },
    };
  }

  /** Public open (the welcome window, probes, other modules). */
  openHelp(page?: string): void { this.open(page); }

  // ── state ───────────────────────────────────────────────────────────────────────────────────────────────────────

  /** Flatten the book with the CURRENT key bindings (a rebind reaches the next open) and rebuild the index. */
  private rebuild(): void {
    this.flat = flatten(BOOK, keyText);
    this.index = buildIndex(this.flat, keyText);
  }

  private loadState(): BookState {
    const ids = new Set(this.flat.map((f) => f.page.id));
    try { return reviveState(localStorage.getItem(STORE), ids); } catch { return { last: null, marks: [] }; }
  }

  private saveState(): void {
    try { localStorage.setItem(STORE, JSON.stringify(this.state)); } catch { /* private mode: the book still works */ }
  }

  private isOpen(): boolean { return !!this.root && this.root.classList.contains('open'); }

  private open(pageId?: string): void {
    if (!this.root) return;
    this.rebuild();
    if (document.pointerLockElement) document.exitPointerLock?.();
    const wasOpen = this.isOpen();
    this.root.classList.add('open');
    const target = pageId ? indexOfPage(this.flat, pageId) : -1;
    if (target >= 0) this.go({ view: 'page', page: target }, wasOpen);
    else if (!wasOpen) {
      const last = this.state.last ? indexOfPage(this.flat, this.state.last) : -1;
      this.history = [];
      this.show(last >= 0 ? { view: 'page', page: last } : { view: 'contents', page: 0 });
    }
    // Focus inside the book so its keys stay out of the game: the search box on the contents, else the page.
    if (this.place.view === 'contents') this.searchInput?.focus(); else this.leaf?.focus();
  }

  private close(): void {
    if (!this.root) return;
    // Give focus back to the page: a hidden control that kept it swallowed the next H (the book never reopened).
    const a = document.activeElement as HTMLElement | null;
    if (a && this.root.contains(a)) a.blur();
    this.root.classList.remove('open');
    this.book?.classList.remove('spine-open');
  }

  /** Move to a place, remembering where we were (for Back). */
  private go(p: Place, push = true): void {
    if (push && this.isOpen() && (p.view !== this.place.view || p.page !== this.place.page)) {
      // Leaving a search result list, Back returns to where the search began (the list itself is not a place).
      const from = this.place.view === 'search' ? this.beforeSearch : this.place;
      if (from) this.history.push(from);
      if (this.history.length > 60) this.history.shift();
    }
    if (this.place.view === 'search') this.beforeSearch = null;
    this.show(p);
  }

  private back(): void {
    const p = this.history.pop();
    if (p) this.show(p);
  }

  private show(p: Place): void {
    const dir = p.view === 'page' && this.place.view === 'page' ? Math.sign(p.page - this.place.page) : 0;
    this.place = p;
    if (p.view === 'page') {
      this.state.last = this.flat[p.page]?.page.id ?? null;
      this.saveState();
    }
    if (p.view !== 'search' && this.searchInput && this.searchInput.value) { this.searchInput.value = ''; this.hits = []; }
    this.renderMast();
    this.renderSpine();
    this.renderLeaf(dir);
    this.book?.classList.remove('spine-open');
    this.keepFocus();
  }

  /**
   * A click re-renders the leaf and removes the button that had focus; focus then fell to <body>, so ← → stopped turning
   * pages and keys reached the game. Keep it inside the book (the leaf), unless the search box has it.
   */
  private keepFocus(): void {
    if (!this.isOpen() || !this.root) return;
    const a = document.activeElement;
    // An EMPTY search box hands focus to the page once you navigate (it was focused on the contents page).
    const idleSearch = a === this.searchInput && !this.searchInput?.value && this.place.view === 'page';
    if (!a || a === document.body || !this.root.contains(a) || idleSearch) this.leaf?.focus({ preventScroll: true });
  }

  // ── building ───────────────────────────────────────────────────────────────────────────────────────────────────

  private buildButton(): void {
    const btn = el('button', 'hb-fab', '?');
    btn.title = 'Field manual (H)';
    btn.setAttribute('aria-label', 'Open the field manual');
    btn.addEventListener('click', () => (this.isOpen() ? this.close() : this.open()));
    document.body.appendChild(btn);
    // HUD LADDER (2026-08-24 overlap audit): in space the sensor radar owns the bottom-right corner — ride above it
    // (and the pulse gauges) while the space HUD is up.
    let inSpace = false;
    window.setInterval(() => {
      const now = atlas.spaceFlight || !!document.getElementById('space-hud');
      if (now === inSpace) return;
      inSpace = now;
      btn.style.bottom = now ? '316px' : '14px';
    }, 500);
  }

  private buildBook(): void {
    installStyle();
    const root = el('div') as HTMLDivElement;
    root.id = 'atlas-help';
    root.dataset.atlasNav = 'help';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', 'Cosmos field manual');
    root.addEventListener('click', (e) => { if (e.target === root) this.close(); });
    root.addEventListener('keydown', (e) => this.onKey(e));

    const book = el('div', 'hb-book') as HTMLDivElement;
    book.dataset.axWindow = '';

    // The masthead: the book's name, the three ways in, search, back, close.
    const mast = el('header', 'hb-mast');
    mast.dataset.axBar = '';
    const spineToggle = el('button', 'hb-spine-toggle', 'Chapters');
    spineToggle.addEventListener('click', () => book.classList.toggle('spine-open'));
    const name = el('div', 'hb-name');
    name.append(el('span', 'hb-name-a', 'Cosmos'), el('span', 'hb-name-b', 'field manual'));
    const nav = el('nav', 'hb-views');
    nav.setAttribute('aria-label', 'Ways into the book');
    const mk = (v: 'contents' | 'index' | 'marks', label: string): HTMLButtonElement => {
      const b = el('button', 'hb-view', label);
      b.dataset.tab = v; // the pad's shoulders walk these (padUiTick reads data-tab + aria-selected)
      b.setAttribute('aria-selected', 'false');
      b.addEventListener('click', () => this.go({ view: v, page: this.place.page }));
      nav.appendChild(b);
      return b;
    };
    this.tabs = { contents: mk('contents', 'Contents'), index: mk('index', 'Index'), marks: mk('marks', 'Marks') };
    const searchWrap = el('label', 'hb-searchwrap');
    const search = el('input', 'hb-search') as HTMLInputElement;
    search.type = 'search';
    search.placeholder = 'Search the manual';
    search.setAttribute('aria-label', 'Search the manual');
    search.addEventListener('input', () => this.onSearch());
    searchWrap.appendChild(search);
    this.searchInput = search;
    const back = el('button', 'hb-back', 'Back');
    back.title = 'Back to the last page (Backspace)';
    back.addEventListener('click', () => this.back());
    this.backBtn = back;
    const close = el('button', 'hb-close', '×');
    close.dataset.atlasClose = '';
    close.title = 'Close (Esc or H)';
    close.setAttribute('aria-label', 'Close the manual');
    close.addEventListener('click', () => this.close());
    mast.append(spineToggle, name, nav, searchWrap, back, close);

    const body = el('div', 'hb-body');
    const spine = el('nav', 'hb-spine');
    spine.setAttribute('aria-label', 'Chapters');
    const leaf = el('main', 'hb-leaf');
    leaf.tabIndex = -1;
    leaf.addEventListener('click', (e) => {
      const a = (e.target as HTMLElement).closest<HTMLElement>('[data-page]');
      if (!a) return;
      e.preventDefault();
      const i = indexOfPage(this.flat, a.dataset.page ?? '');
      if (i >= 0) this.go({ view: 'page', page: i });
    });
    body.append(spine, leaf);
    book.append(mast, body);
    root.appendChild(book);
    document.body.appendChild(root);
    this.root = root;
    this.book = book;
    this.spine = spine;
    this.leaf = leaf;
  }

  // ── keys & search ──────────────────────────────────────────────────────────────────────────────────────────────

  private onKey(e: KeyboardEvent): void {
    if (!this.isOpen()) return; // a closed book answers nothing (and stops nothing)
    const inSearch = e.target === this.searchInput;
    const typing = inSearch || ['INPUT', 'TEXTAREA', 'SELECT'].includes((e.target as HTMLElement | null)?.tagName ?? '');
    if (e.key === 'Escape') {
      // In the search box Esc clears the query first (the Esc order yields to a focused field), then closes the book.
      if (inSearch) {
        e.stopPropagation();
        if (this.searchInput!.value) { this.searchInput!.value = ''; this.onSearch(); } else this.close();
      }
      return; // outside a field the one Esc order already closed the top nav panel — this book
    }
    e.stopPropagation(); // the book is reading, not playing: no key inside it reaches the game
    if (inSearch) {
      if (e.key === 'Enter' && this.hits.length) { e.preventDefault(); this.go({ view: 'page', page: this.hits[0].index }); this.leaf?.focus(); }
      else if (e.key === 'ArrowDown' && this.hits.length) { e.preventDefault(); this.leaf?.querySelector<HTMLElement>('.hb-hit')?.focus(); }
      // An empty box has no caret to move: the arrows turn pages there too.
      else if (!this.searchInput!.value && (e.key === 'ArrowRight' || e.key === 'ArrowLeft' || e.key === 'PageDown' || e.key === 'PageUp')) {
        e.preventDefault();
        this.turn(e.key === 'ArrowRight' || e.key === 'PageDown' ? 1 : -1);
      }
      return;
    }
    if (typing || e.ctrlKey || e.metaKey || e.altKey) return;
    switch (e.key) {
      case 'ArrowRight': case 'PageDown': e.preventDefault(); this.turn(1); break;
      case 'ArrowLeft': case 'PageUp': e.preventDefault(); this.turn(-1); break;
      case 'Home': e.preventDefault(); this.go({ view: 'contents', page: this.place.page }); break;
      case '/': e.preventDefault(); this.searchInput?.focus(); break;
      case 'Backspace': e.preventDefault(); this.back(); break;
      case 'h': case 'H': e.preventDefault(); if (!e.repeat) this.close(); break;
      default: break;
    }
  }

  /** Turn one page (from the contents, → opens page 1). */
  private turn(d: number): void {
    if (this.place.view !== 'page') { if (d > 0 && this.place.view === 'contents') this.go({ view: 'page', page: 0 }); return; }
    const n = this.place.page + d;
    if (n >= 0 && n < this.flat.length) this.go({ view: 'page', page: n });
  }

  private onSearch(): void {
    const q = this.searchInput?.value.trim() ?? '';
    if (!q) {
      this.hits = [];
      const p = this.beforeSearch ?? { view: 'contents' as View, page: this.place.page };
      this.beforeSearch = null;
      this.show(p);
      return;
    }
    if (this.place.view !== 'search') this.beforeSearch = this.place;
    this.hits = search(this.flat, q);
    this.place = { view: 'search', page: this.place.page };
    this.renderMast();
    this.renderSpine();
    this.renderLeaf(0);
  }

  // ── rendering ──────────────────────────────────────────────────────────────────────────────────────────────────

  private renderMast(): void {
    if (!this.tabs) return;
    for (const [v, b] of Object.entries(this.tabs) as Array<['contents' | 'index' | 'marks', HTMLButtonElement]>) {
      b.setAttribute('aria-selected', String(this.place.view === v));
    }
    this.tabs.marks.textContent = this.state.marks.length ? `Marks ${this.state.marks.length}` : 'Marks';
    if (this.backBtn) this.backBtn.disabled = this.history.length === 0;
  }

  private renderSpine(): void {
    const spine = this.spine;
    if (!spine) return;
    spine.replaceChildren();
    const current = this.place.view === 'page' ? this.flat[this.place.page] : null;
    const folios = chapterFolios(this.flat);
    let chapterNo = 0;
    for (const part of BOOK) {
      const ph = el('div', 'hb-part');
      ph.append(el('span', 'num', part.numeral), el('span', '', part.title));
      spine.appendChild(ph);
      for (const ch of part.chapters) {
        chapterNo++;
        const open = current?.chapter.id === ch.id;
        const b = el('button', 'hb-ch');
        b.setAttribute('aria-expanded', String(open));
        b.append(el('span', 'n', String(chapterNo)), el('span', 't', ch.title));
        const first = (folios.get(ch.id) ?? 1) - 1;
        b.addEventListener('click', () => this.go({ view: 'page', page: first }));
        spine.appendChild(b);
        if (!open) continue;
        const list = el('div', 'hb-pages');
        ch.pages.forEach((pg, k) => {
          const i = first + k;
          const pb = el('button', 'hb-pg', pg.title.replace(/\{\{key:[A-Za-z]+\}\}/g, (m) => fillKeys(m, keyText, false)));
          if (i === this.place.page) pb.setAttribute('aria-current', 'page');
          pb.addEventListener('click', () => this.go({ view: 'page', page: i }));
          list.appendChild(pb);
        });
        spine.appendChild(list);
      }
    }
    spine.querySelector('[aria-current="page"]')?.scrollIntoView({ block: 'nearest' });
  }

  private renderLeaf(dir: number): void {
    const leaf = this.leaf;
    if (!leaf) return;
    leaf.replaceChildren();
    const sheet = el('div', 'hb-sheet');
    switch (this.place.view) {
      case 'contents': this.renderContents(sheet); break;
      case 'index': this.renderIndex(sheet); break;
      case 'marks': this.renderMarks(sheet); break;
      case 'search': this.renderSearch(sheet); break;
      default: this.renderPage(sheet); break;
    }
    leaf.appendChild(sheet);
    leaf.scrollTop = 0;
    if (dir !== 0) {
      sheet.style.setProperty('--hb-dx', `${dir * 12}px`);
      sheet.classList.add('turning');
    }
  }

  private renderContents(sheet: HTMLElement): void {
    sheet.classList.add('hb-cover');
    sheet.append(el('h1', 'hb-cover-title', 'Field manual'));
    sheet.append(el('p', 'hb-cover-sub', `Everything a traveler can do in Cosmos — ${BOOK.reduce((n, p) => n + p.chapters.length, 0)} chapters, ${this.flat.length} pages. Search above, browse the chapters, or look a word up in the index.`));
    const start = el('div', 'hb-start');
    start.append(el('span', 'hb-start-label', 'Start here'));
    for (const id of START_HERE) {
      const i = indexOfPage(this.flat, id);
      if (i < 0) continue;
      const b = el('button', 'hb-link', this.flat[i].page.title);
      b.addEventListener('click', () => this.go({ view: 'page', page: i }));
      start.appendChild(b);
    }
    sheet.appendChild(start);
    const folios = chapterFolios(this.flat);
    let chapterNo = 0;
    for (const part of BOOK) {
      const sec = el('section', 'hb-toc-part');
      const h = el('h2');
      h.append(el('span', 'num', `Part ${part.numeral}`), el('span', '', part.title));
      sec.appendChild(h);
      for (const ch of part.chapters) {
        chapterNo++;
        const f = folios.get(ch.id) ?? 1;
        const row = el('button', 'hb-toc-row');
        row.append(el('span', 'n', String(chapterNo)), el('span', 't', ch.title), el('span', 'lead'), el('span', 'f', String(f)));
        row.addEventListener('click', () => this.go({ view: 'page', page: f - 1 }));
        sec.append(row, el('p', 'hb-toc-blurb', ch.blurb));
      }
      sheet.appendChild(sec);
    }
  }

  private renderPage(sheet: HTMLElement): void {
    const f = this.flat[this.place.page];
    if (!f) { this.renderContents(sheet); return; }
    const marked = this.state.marks.includes(f.page.id);
    if (marked) sheet.appendChild(el('div', 'hb-ribbon'));
    const head = el('div', 'hb-runhead');
    const left = el('span');
    left.append(el('span', 'chn', String(f.chapterNo)), el('span', '', f.chapter.title));
    head.append(left, el('span', 'hb-folio', `${f.folio} / ${this.flat.length}`));
    sheet.appendChild(head);
    sheet.appendChild(el('h2', 'hb-ptitle', fillKeys(f.page.title, keyText, false)));
    const text = el('div', 'hb-text');
    setHtml(text, fillKeys(f.page.body, keyText, true)); // trusted static content authored in lib/help
    for (const a of text.querySelectorAll<HTMLAnchorElement>('a[data-page]')) { a.href = '#'; }
    sheet.appendChild(text);
    const see = (f.page.see ?? []).map((id) => indexOfPage(this.flat, id)).filter((i) => i >= 0);
    if (see.length) {
      const s = el('div', 'hb-see');
      s.appendChild(el('span', 'hb-see-label', 'See also'));
      for (const i of see) {
        const b = el('button', 'hb-link', this.flat[i].page.title.replace(/\{\{key:[A-Za-z]+\}\}/g, (m) => fillKeys(m, keyText, false)));
        b.addEventListener('click', () => this.go({ view: 'page', page: i }));
        s.appendChild(b);
      }
      sheet.appendChild(s);
    }
    // The page turn: the neighbours by name and folio, and the bookmark.
    const turn = el('nav', 'hb-turn');
    turn.setAttribute('aria-label', 'Turn the page');
    const prev = this.flat[this.place.page - 1];
    const next = this.flat[this.place.page + 1];
    const pb = el('button', 'prev');
    if (prev) {
      pb.append(el('span', 'g', '‹'), el('span', 'f', String(prev.folio)), el('span', 'tt', fillKeys(prev.page.title, keyText, false)));
      pb.addEventListener('click', () => this.go({ view: 'page', page: this.place.page - 1 }));
    } else pb.disabled = true;
    const mark = el('button', 'hb-mark', marked ? 'Marked' : 'Mark this page');
    mark.setAttribute('aria-pressed', String(marked));
    mark.addEventListener('click', () => this.toggleMark(f.page.id));
    const nb = el('button', 'next');
    if (next) {
      nb.append(el('span', 'tt', fillKeys(next.page.title, keyText, false)), el('span', 'f', String(next.folio)), el('span', 'g', '›'));
      nb.addEventListener('click', () => this.go({ view: 'page', page: this.place.page + 1 }));
    } else nb.disabled = true;
    turn.append(pb, mark, nb);
    sheet.appendChild(turn);
  }

  private toggleMark(id: string): void {
    const at = this.state.marks.indexOf(id);
    if (at >= 0) this.state.marks.splice(at, 1); else this.state.marks.push(id);
    this.saveState();
    this.renderMast();
    this.renderLeaf(0);
    this.leaf?.querySelector<HTMLElement>('.hb-mark')?.focus();
  }

  private renderIndex(sheet: HTMLElement): void {
    sheet.append(el('h1', 'hb-cover-title hb-small', 'Index'));
    sheet.append(el('p', 'hb-cover-sub', `${this.index.length} terms. Pick a letter, then a page number.`));
    const letters = indexLetters(this.index);
    const strip = el('div', 'hb-letters');
    for (const [c, live] of letters) {
      const b = el('button', '', c);
      b.disabled = !live;
      b.addEventListener('click', () => {
        // Land the heading just under the sticky strip, whose height follows the width (1–3 rows of letters): a fixed
        // scroll-margin left the previous row's last pixels peeking out under the strip.
        const h = sheet.querySelector<HTMLElement>(`[data-letter="${c}"]`);
        const leaf = sheet.parentElement;
        if (!h || !leaf) return;
        const top = h.getBoundingClientRect().top - leaf.getBoundingClientRect().top + leaf.scrollTop;
        leaf.scrollTop = Math.max(0, top - strip.offsetHeight - 24);
      });
      strip.appendChild(b);
    }
    sheet.appendChild(strip);
    let cur = '';
    for (const e of this.index) {
      const L = letterOf(e.term);
      if (L !== cur) {
        cur = L;
        const h = el('h3', 'hb-ix-letter', L === '#' ? 'Numbers and symbols' : L);
        h.dataset.letter = L;
        sheet.appendChild(h);
      }
      const row = el('div', 'hb-ix-row');
      row.appendChild(el('span', 't', e.term));
      const fs = el('span', 'fs');
      for (const f of e.folios) {
        const b = el('button', '', String(f));
        b.title = this.flat[f - 1]?.page.title ?? '';
        b.addEventListener('click', () => this.go({ view: 'page', page: f - 1 }));
        fs.appendChild(b);
      }
      row.appendChild(fs);
      sheet.appendChild(row);
    }
  }

  private renderMarks(sheet: HTMLElement): void {
    sheet.append(el('h1', 'hb-cover-title hb-small', 'Marks'));
    const marks = this.state.marks.map((id) => indexOfPage(this.flat, id)).filter((i) => i >= 0).sort((a, b) => a - b);
    if (!marks.length) {
      sheet.append(el('p', 'hb-cover-sub', 'No marked pages yet. Press “Mark this page” at the foot of any page and it will be kept here.'));
      return;
    }
    sheet.append(el('p', 'hb-cover-sub', `${marks.length} marked ${marks.length === 1 ? 'page' : 'pages'}, in book order.`));
    for (const i of marks) {
      const f = this.flat[i];
      const row = el('button', 'hb-toc-row');
      row.append(el('span', 'n', String(f.chapterNo)), el('span', 't', fillKeys(f.page.title, keyText, false)), el('span', 'lead'), el('span', 'f', String(f.folio)));
      row.addEventListener('click', () => this.go({ view: 'page', page: i }));
      sheet.append(row, el('p', 'hb-toc-blurb', f.chapter.title));
    }
  }

  private renderSearch(sheet: HTMLElement): void {
    const q = this.searchInput?.value.trim() ?? '';
    if (!this.hits.length) {
      sheet.append(el('h1', 'hb-cover-title hb-small', 'No pages match'));
      sheet.append(el('p', 'hb-cover-sub', `Nothing mentions every word of “${q}”. Try fewer or shorter words, or look in the Index.`));
      return;
    }
    sheet.append(el('p', 'hb-results', `${this.hits.length} ${this.hits.length === 1 ? 'page mentions' : 'pages mention'} “${q}”`));
    for (const h of this.hits) {
      const f = this.flat[h.index];
      const b = el('button', 'hb-hit');
      const top = el('span', 'h');
      top.append(el('span', '', fillKeys(f.page.title, keyText, false)));
      const where = el('span', 'w', `${f.chapter.title}, page ${f.folio}`);
      const snip = el('span', 's');
      let at = 0;
      for (const [a, z] of h.marks) {
        if (a > at) snip.appendChild(document.createTextNode(h.snippet.slice(at, a)));
        snip.appendChild(el('mark', '', h.snippet.slice(a, z)));
        at = z;
      }
      if (at < h.snippet.length) snip.appendChild(document.createTextNode(h.snippet.slice(at)));
      b.append(top, where, snip);
      b.addEventListener('click', () => this.go({ view: 'page', page: h.index }));
      b.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown') { e.preventDefault(); (b.nextElementSibling as HTMLElement | null)?.focus(); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); const p = b.previousElementSibling as HTMLElement | null; (p?.classList.contains('hb-hit') ? p : this.searchInput)?.focus(); }
      });
      sheet.appendChild(b);
    }
  }

  dispose(): void {
    this.root?.remove();
    document.querySelector('.hb-fab')?.remove();
  }
}

/**
 * Render trusted static HTML without touching a live element's innerHTML: DOMParser builds an inert document (no
 * script runs), and the parsed nodes are appended. The content is authored in lib/help (verify:help-book forbids
 * handlers, scripts and javascript: links).
 */
function setHtml(target: HTMLElement, html: string): void {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  target.append(...Array.from(doc.body.childNodes));
}

/** The book's one stylesheet, scoped under #atlas-help: tokens only (THE TOKEN LAW), so all three themes dress it. */
function installStyle(): void {
  if (document.getElementById('hb-style')) return;
  const s = document.createElement('style');
  s.id = 'hb-style';
  s.textContent = `
.hb-fab{position:fixed;right:14px;bottom:14px;z-index:90;width:38px;height:38px;border-radius:50%;cursor:pointer;
  background:color-mix(in srgb, var(--ax-bg2) 92%, transparent);color:var(--ax-hi);border:1px solid var(--ax-line2);
  font:700 18px var(--ax-display);box-shadow:var(--ax-shadow)}
.hb-fab:focus-visible{outline:2px solid var(--ax-accent);outline-offset:2px}
#atlas-help{position:fixed;inset:0;z-index:120;display:none;align-items:center;justify-content:center;background:var(--ax-scrim)}
#atlas-help.open{display:flex}
#atlas-help .hb-book{width:min(1120px,94vw);height:min(780px,90vh);display:grid;grid-template-rows:auto minmax(0,1fr);
  overflow:hidden;position:relative;font:14.5px/1.65 var(--ax-font);color:var(--ax-text)}
#atlas-help button{font:inherit;color:inherit}
#atlas-help button:focus-visible,#atlas-help input:focus-visible,#atlas-help a:focus-visible,#atlas-help .hb-leaf:focus-visible{outline:2px solid var(--ax-accent);outline-offset:2px}
#atlas-help .hb-leaf:focus{outline:none}
/* the masthead */
#atlas-help .hb-mast{display:flex;align-items:center;gap:6px;padding:9px 12px 9px 16px}
#atlas-help .hb-name{display:flex;align-items:baseline;gap:7px;margin-right:12px;white-space:nowrap}
#atlas-help .hb-name-a{font:600 19px/1 var(--ax-display);color:var(--ax-hi);letter-spacing:.02em}
#atlas-help .hb-name-b{font:13px/1 var(--ax-font);color:var(--ax-muted)}
#atlas-help .hb-views{display:flex;gap:2px}
#atlas-help .hb-view,#atlas-help .hb-back,#atlas-help .hb-spine-toggle{background:none;border:0;border-radius:var(--ax-radius);
  padding:6px 10px;font:13px var(--ax-font);color:var(--ax-soft);cursor:pointer;white-space:nowrap}
#atlas-help .hb-view:hover,#atlas-help .hb-back:hover:not(:disabled),#atlas-help .hb-spine-toggle:hover{color:var(--ax-text);background:var(--ax-bg3)}
#atlas-help .hb-view[aria-selected="true"]{color:var(--ax-text);box-shadow:inset 0 -2px 0 var(--ax-accent)}
#atlas-help .hb-back:disabled{opacity:.4;cursor:default}
#atlas-help .hb-searchwrap{flex:1;min-width:120px;margin:0 6px}
#atlas-help .hb-search{width:100%;box-sizing:border-box;background:var(--ax-bg);color:var(--ax-text);border:1px solid var(--ax-line2);
  border-radius:var(--ax-radius);padding:7px 11px;font:14px var(--ax-font)}
#atlas-help .hb-search::placeholder{color:var(--ax-muted)}
#atlas-help .hb-search::-webkit-search-cancel-button{-webkit-appearance:none;appearance:none} /* Esc clears; the native × ignored the theme */
#atlas-help .hb-close{background:none;border:0;width:34px;height:34px;border-radius:var(--ax-radius);font:22px/1 var(--ax-font);color:var(--ax-muted);cursor:pointer}
#atlas-help .hb-close:hover{background:var(--ax-bg3);color:var(--ax-text)}
#atlas-help .hb-spine-toggle{display:none}
/* the body: the spine and the leaf */
#atlas-help .hb-body{display:grid;grid-template-columns:268px minmax(0,1fr);min-height:0;position:relative}
#atlas-help .hb-spine{overflow-y:auto;border-right:1px solid var(--ax-line);padding:6px 0 28px}
#atlas-help .hb-part{display:flex;gap:8px;align-items:baseline;padding:16px 18px 5px;font:12.5px var(--ax-font);color:var(--ax-muted)}
#atlas-help .hb-part .num{font:12px var(--ax-mono);color:var(--ax-accent);min-width:1.9em}
#atlas-help .hb-ch{display:flex;gap:9px;align-items:baseline;width:100%;text-align:left;background:none;border:0;
  padding:5px 16px 5px 18px;font:13.5px/1.35 var(--ax-font);color:var(--ax-soft);cursor:pointer}
#atlas-help .hb-ch .n{font:12px var(--ax-mono);color:var(--ax-muted);min-width:1.9em;text-align:right}
#atlas-help .hb-ch:hover{color:var(--ax-text)}
#atlas-help .hb-ch[aria-expanded="true"]{color:var(--ax-text);font-weight:600}
#atlas-help .hb-pages{margin:1px 0 6px}
#atlas-help .hb-pg{display:block;width:100%;text-align:left;background:none;border:0;border-left:2px solid transparent;
  padding:4px 14px 4px 50px;font:13px/1.35 var(--ax-font);color:var(--ax-muted);cursor:pointer}
#atlas-help .hb-pg:hover{color:var(--ax-text)}
#atlas-help .hb-pg[aria-current="page"]{color:var(--ax-text);border-left-color:var(--ax-accent);
  background:color-mix(in srgb, var(--ax-accent) 9%, transparent)}
#atlas-help .hb-leaf{overflow-y:auto;position:relative}
#atlas-help .hb-sheet{position:relative;max-width:68ch;margin:0 auto;padding:22px 34px 30px}
@keyframes hbTurn{from{opacity:0;transform:translateX(var(--hb-dx,10px))}to{opacity:1;transform:none}}
#atlas-help .hb-sheet.turning{animation:hbTurn .18s ease-out}
/* a page */
#atlas-help .hb-runhead{display:flex;justify-content:space-between;align-items:baseline;gap:12px;padding-bottom:7px;
  margin:0 0 20px;border-bottom:1px solid var(--ax-line);font-size:12.5px;color:var(--ax-muted)}
#atlas-help .hb-runhead .chn{font-family:var(--ax-mono);color:var(--ax-accent);margin-right:9px}
#atlas-help .hb-folio{font-family:var(--ax-mono)}
#atlas-help .hb-ptitle{font:600 29px/1.15 var(--ax-display);color:var(--ax-hi);margin:0 0 16px;letter-spacing:.01em}
#atlas-help .hb-text p{margin:0 0 .85em}
#atlas-help .hb-text ul,#atlas-help .hb-text ol{margin:0 0 .95em;padding-left:1.35em}
#atlas-help .hb-text li{margin:.3em 0}
#atlas-help .hb-text li::marker{color:var(--ax-muted)}
#atlas-help .hb-text b{color:var(--ax-text);font-weight:650}
#atlas-help .hb-text i{color:var(--ax-soft)}
#atlas-help .hb-text table{width:100%;border-collapse:collapse;margin:.3em 0 1.1em;font-size:13.5px;line-height:1.5}
#atlas-help .hb-text td{padding:6px 12px 6px 0;border-bottom:1px solid var(--ax-line);vertical-align:top}
#atlas-help .hb-text td:first-child{white-space:nowrap;color:var(--ax-soft);width:1%}
#atlas-help kbd{display:inline-block;font:12px/1.5 var(--ax-mono);padding:0 6px;margin:0 1px;border:1px solid var(--ax-line2);
  border-bottom-width:2px;border-radius:var(--ax-radius);background:var(--ax-bg3);color:var(--ax-text);white-space:nowrap}
#atlas-help code{font:12.5px var(--ax-mono);color:var(--ax-hi)}
#atlas-help .hb-text a[data-page],#atlas-help .hb-link{background:none;border:0;padding:0;cursor:pointer;color:var(--ax-hi);
  text-decoration:underline;text-decoration-color:color-mix(in srgb, var(--ax-hi) 45%, transparent);text-underline-offset:3px}
#atlas-help .hb-see{display:flex;flex-wrap:wrap;gap:6px 16px;align-items:baseline;margin-top:24px;padding-top:12px;
  border-top:1px solid var(--ax-line);font-size:13.5px}
#atlas-help .hb-see-label{color:var(--ax-muted);margin-right:2px}
#atlas-help .hb-turn{display:grid;grid-template-columns:1fr auto 1fr;gap:10px;align-items:center;margin-top:28px;padding-top:12px;
  border-top:1px solid var(--ax-line)}
#atlas-help .hb-turn .prev,#atlas-help .hb-turn .next{display:flex;gap:8px;align-items:baseline;background:none;border:0;
  border-radius:var(--ax-radius);padding:7px 8px;font-size:13.5px;color:var(--ax-soft);cursor:pointer;min-width:0}
#atlas-help .hb-turn .next{justify-content:flex-end;text-align:right}
#atlas-help .hb-turn .prev:hover:not(:disabled),#atlas-help .hb-turn .next:hover:not(:disabled){background:var(--ax-bg3);color:var(--ax-text)}
#atlas-help .hb-turn button:disabled{visibility:hidden}
#atlas-help .hb-turn .tt{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
#atlas-help .hb-turn .f{font:12px var(--ax-mono);color:var(--ax-muted)}
#atlas-help .hb-turn .g{color:var(--ax-accent);font-size:17px;line-height:1}
#atlas-help .hb-mark{background:none;border:1px solid var(--ax-line2);border-radius:var(--ax-radius);padding:6px 12px;
  font-size:12.5px;color:var(--ax-soft);cursor:pointer;white-space:nowrap}
#atlas-help .hb-mark[aria-pressed="true"]{border-color:var(--ax-accent);color:var(--ax-accent)}
#atlas-help .hb-ribbon{position:absolute;top:0;right:7px;width:20px;height:56px;background:var(--ax-accent);
  clip-path:polygon(0 0,100% 0,100% 100%,50% 76%,0 100%);box-shadow:var(--ax-shadow)}
/* the contents (the book's front page) */
#atlas-help .hb-cover-title{font:600 50px/1.02 var(--ax-display);color:var(--ax-hi);margin:8px 0 10px;letter-spacing:.01em}
#atlas-help .hb-cover-title.hb-small{font-size:34px}
#atlas-help .hb-cover-sub{color:var(--ax-soft);margin:0 0 16px;max-width:60ch}
#atlas-help .hb-start{display:flex;flex-wrap:wrap;gap:6px 16px;align-items:baseline;padding:10px 0 4px;margin-bottom:6px;
  border-top:1px solid var(--ax-line);border-bottom:1px solid var(--ax-line);font-size:14px}
#atlas-help .hb-start-label{color:var(--ax-muted)}
#atlas-help .hb-toc-part{margin-top:26px}
#atlas-help .hb-toc-part h2{display:flex;gap:10px;align-items:baseline;margin:0 0 6px;font:600 15px var(--ax-font);color:var(--ax-soft)}
#atlas-help .hb-toc-part h2 .num{font:12.5px var(--ax-mono);color:var(--ax-accent)}
#atlas-help .hb-toc-row{display:flex;gap:10px;align-items:baseline;width:100%;background:none;border:0;padding:6px 0 0;
  text-align:left;font:16px/1.3 var(--ax-font);color:var(--ax-text);cursor:pointer}
#atlas-help .hb-toc-row:hover .t{color:var(--ax-hi)}
#atlas-help .hb-toc-row .n{font:13px var(--ax-mono);color:var(--ax-accent);min-width:2.2em;text-align:right}
#atlas-help .hb-toc-row .lead{flex:1;min-width:24px;border-bottom:2px dotted var(--ax-line2);transform:translateY(-5px)}
#atlas-help .hb-toc-row .f{font:13px var(--ax-mono);color:var(--ax-muted)}
#atlas-help .hb-toc-blurb{margin:1px 0 4px 3.25em;font-size:13px;line-height:1.45;color:var(--ax-muted)}
/* the index */
#atlas-help .hb-letters{position:sticky;top:0;z-index:1;display:flex;flex-wrap:wrap;gap:3px;padding:10px 0;margin:0 0 4px;
  background:var(--ax-bg2);border-bottom:1px solid var(--ax-line)}
#atlas-help .hb-letters button{min-width:28px;padding:4px 0;background:none;border:1px solid var(--ax-line);border-radius:var(--ax-radius);
  font:12px var(--ax-mono);color:var(--ax-soft);cursor:pointer}
#atlas-help .hb-letters button:hover:not(:disabled){border-color:var(--ax-accent);color:var(--ax-text)}
#atlas-help .hb-letters button:disabled{opacity:.3;cursor:default}
#atlas-help .hb-ix-letter{margin:20px 0 6px;font:600 23px var(--ax-display);color:var(--ax-accent);scroll-margin-top:104px}
#atlas-help .hb-ix-row{display:flex;gap:12px;align-items:baseline;padding:3px 0;border-bottom:1px dotted var(--ax-line)}
#atlas-help .hb-ix-row .t{flex:1}
#atlas-help .hb-ix-row .fs{display:flex;flex-wrap:wrap;gap:2px 8px;justify-content:flex-end}
#atlas-help .hb-ix-row .fs button{background:none;border:0;padding:0 2px;font:12.5px var(--ax-mono);color:var(--ax-hi);cursor:pointer;
  text-decoration:underline;text-decoration-color:color-mix(in srgb, var(--ax-hi) 40%, transparent);text-underline-offset:3px}
/* search */
#atlas-help .hb-results{color:var(--ax-muted);margin:0 0 6px}
#atlas-help .hb-hit{display:flex;flex-direction:column;gap:2px;width:100%;text-align:left;background:none;border:0;
  border-bottom:1px solid var(--ax-line);padding:11px 6px;cursor:pointer;color:var(--ax-text);border-radius:0}
#atlas-help .hb-hit:hover{background:color-mix(in srgb, var(--ax-bg3) 60%, transparent)}
#atlas-help .hb-hit .h{font:600 15.5px/1.3 var(--ax-font)}
#atlas-help .hb-hit .w{font-size:12.5px;color:var(--ax-muted)}
#atlas-help .hb-hit .s{font-size:13.5px;line-height:1.5;color:var(--ax-soft)}
#atlas-help mark{background:color-mix(in srgb, var(--ax-accent) 34%, transparent);color:inherit;border-radius:var(--ax-radius);padding:0 1px}
@media (prefers-reduced-motion: reduce){#atlas-help .hb-sheet.turning{animation:none}}
@media (max-width: 760px){
  #atlas-help .hb-book{width:100vw;height:100vh;border-radius:0;box-sizing:border-box}
  #atlas-help .hb-name{display:none}
  #atlas-help .hb-mast{flex-wrap:wrap;row-gap:6px;column-gap:2px;padding:8px 10px}
  #atlas-help .hb-back,#atlas-help .hb-spine-toggle{padding:6px 7px}
  #atlas-help .hb-back{margin-left:auto} /* Back and × hold the top-right corner; at 390 px a 6px gap wrapped the × onto a row of its own */
  #atlas-help .hb-searchwrap{order:10;flex:1 0 100%;margin:0}
  #atlas-help .hb-spine-toggle{display:inline-block}
  #atlas-help .hb-views .hb-view{padding:6px 7px}
  #atlas-help .hb-body{grid-template-columns:minmax(0,1fr)}
  #atlas-help .hb-spine{position:absolute;top:0;bottom:0;left:0;z-index:3;width:min(300px,86vw);background:var(--ax-win-bg);
    border-right:1px solid var(--ax-line2);transform:translateX(-102%);transition:transform .2s ease}
  #atlas-help .hb-book.spine-open .hb-spine{transform:none}
  #atlas-help .hb-sheet{padding:16px 16px 24px}
  #atlas-help .hb-cover-title{font-size:38px}
  #atlas-help .hb-turn{grid-template-columns:1fr 1fr}
  #atlas-help .hb-turn .hb-mark{grid-column:1 / -1;order:-1;justify-self:center}
}
@media (max-width: 760px) and (prefers-reduced-motion: reduce){#atlas-help .hb-spine{transition:none}}
`;
  document.head.appendChild(s);
}
